<template>
<div>
  <v-row>
    <page-title :value="$t('nap.title')"></page-title>
    <v-spacer />
    <v-btn color="primary" variant="flat" to="/nap/create">
      <v-icon start>mdi-plus</v-icon>
      {{ $t('nap.create') }}
    </v-btn>
  </v-row>

  <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
    <v-container fluid>
      <v-row align="center">
        <v-col cols="12" class="pb-0">
          <h5>{{ $t('nap.filterByDate') }}</h5>
        </v-col>
        <v-col cols="3" class="pe-4">
          <date-picker v-model="from" :label="$t('nap.startDate')"></date-picker>
        </v-col>
        <v-col cols="3" class="ps-4">
          <date-picker v-model="to" :label="$t('nap.endDate')"></date-picker>
        </v-col>
        <v-col cols="6">
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="4">
          <h5>{{ $t('common.class') }}</h5>
          <class-picker
            search
            :classes="classes"
            v-model="classId"
          ></class-picker>
        </v-col>
        <v-col cols="4">
          <h5>{{ $t('common.age') }}</h5>
          <age-picker v-model="age"></age-picker>
        </v-col>
        <v-col cols="4">
          <v-btn
            color="primary"
            variant="flat"
            class="mt-4 search-button"
            @click="search"
            :disabled="isSearching"
          >{{ $t('common.search') }}</v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-card>

  <div>
    <v-progress-linear
      v-if="isSearching"
      indeterminate
      color="primary"
      class="my-8"
    ></v-progress-linear>
  </div>

  <div class="pt-10">
    <v-table>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              {{ $t('common.date') }}
            </th>
            <th class="text-left">
              {{ $t('nap.target') }}
            </th>
            <th class="text-left">
              {{ $t('nap.interval') }}
            </th>
            <th class="text-left">
              {{ $t('nap.headcount') }}
            </th>
            <th class="text-left">
              {{ $t('common.status') }}
            </th>
            <th class="text-left">
              {{ $t('nap.napEndTime') }}
            </th>
            <th class="text-left">
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="n in napList"
            :key="n.napId"
          >
            <td>
              {{ formatDateOnly(n.date) }}
            </td>
            <td>
              {{ formatTarget(n) }}
            </td>
            <td>
              {{ formatInterval(n.interval) }}
            </td>
            <td>
              {{ n.childCount }}
            </td>
            <td>
              {{ formartStatus(n.status) }}
            </td>
            <td>
              {{ formartDate(n.finishedAt) }}
            </td>
            <td>
              <v-btn
                variant="flat"
                color="primary"
                class="ma-2"
                :to="`/nap/${n.napId}/detail`"
              >{{ $t('common.detail') }}</v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>
  </div>
</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PageTitle from '@/components/atoms/PageTitle.vue'
import ClassPicker from '@/components/atoms/ClassPicker.vue'
import AgePicker from '@/components/atoms/AgePicker.vue'
import DatePicker from '@/components/atoms/DatePicker.vue'

import LocalDataService from '@/service/LocalDataService'
import { classModule } from '@/store/dataModules/classModule'
import { NurseryClassResponse, NurseryNapResponse } from 'chaild-api/lib'

import { napListModule } from '@/store/dataModules/napListModule';
import { formatAgeFilterLabel } from '@/constants/attendance';
import dayjs from 'dayjs'

interface DataType {
  isSearching: boolean;
}

export default defineComponent({
  name: 'NapList',

  // todo: 午睡チェックの右上おしたら、新規作成を挟む（そこでテンプレートも読み込める）
  components: {
    PageTitle,
    ClassPicker,
    AgePicker,
    DatePicker,
  },

  data: (): DataType => {
    return {
      isSearching: false,
    };
  },

  computed: {
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    napList(): NurseryNapResponse[] {
      return napListModule.naps;
    },
    from: {
      get(): string | null {
        return napListModule.from;
      },
      set(date: string) {
        napListModule.setFrom(date);
      },
    },
    to: {
      get(): string | null {
        return napListModule.to;
      },
      set(date: string) {
        napListModule.setTo(date);
      },
    },
    classId: {
      get(): number | null {
        return napListModule.classId;
      },
      set(value: number | null) {
        napListModule.setClassId(value);
      }
    },
    age: {
      get(): number | null {
        return napListModule.age;
      },
      set(value: number | null) {
        napListModule.setAge(value);
      }
    },
  },

  methods: {
    async search() {
      this.isSearching = true;
      if (this.from) sessionStorage.setItem('napFrom', this.from); else sessionStorage.setItem('napFrom', '');
      if (this.to) sessionStorage.setItem('napTo', this.to); else sessionStorage.setItem('napTo', '');
      if (this.classId) sessionStorage.setItem('napClassId', String(this.classId)); else sessionStorage.setItem('napClassId', '');
      if (this.age) sessionStorage.setItem('napAge', String(this.age)); else sessionStorage.setItem('napAge', '');

      // classIdがallの時にエラーが出る対策
      if (this.classId && isNaN(this.classId)) this.classId = null;

      await napListModule.listNaps();
      this.isSearching = false;
    },
    formatTarget(nap: NurseryNapResponse) {
      const classLabel = nap.class.all ?
        this.$t('nap.allAges') :
        nap.class.classes.map((c) => c.className).join(', ');
      const ageLabel = nap.age.all ?
        this.$t('nap.allAges') :
        nap.age.ages.map((a) => formatAgeFilterLabel(a)).join(', ');
      return `${classLabel} / ${ageLabel}`;
    },
    formatInterval(value: number) {
      switch (value) {
        case 0:
          return this.$t('nap.intervals.five');
        case 1:
          return this.$t('nap.intervals.ten');
        case 2:
          return this.$t('nap.intervals.fifteen');
        case 3:
          return this.$t('nap.intervals.thirty');
        case 4:
          return this.$t('nap.intervals.sixty');
        default:
          return '';
      }
    },
    formartStatus(value: string) {
      switch (value) {
        case 'inProgress':
          return this.$t('nap.status.inProgress');
        case 'finished':
          return this.$t('nap.status.finished');
        case 'applied':
          return this.$t('nap.status.applied');
        default:
          return '';
      }
    },
    formatDateOnly(value: string | null | undefined) {
      if (value) {
        return dayjs(value).format('DD/MM/YYYY');
      }
      return '';
    },
    formartDate(value: string | null | undefined) {
      if (value) {
        return dayjs(value).format('DD/MM/YYYY HH:mm');
      }
      return '';
    },
    openNewTab(value: string) {
      window.open(value, '_blank');
    },
  },

  mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      classModule.listClass({
        nurseryId: nurseryId,
      });
      const napFrom = sessionStorage.getItem('napFrom')
      const napTo = sessionStorage.getItem('napTo')
      const napClassId = sessionStorage.getItem('napClassId')
      const napAge = sessionStorage.getItem('napAge')

      if (napFrom) this.from = napFrom;
      if (napTo) this.to = napTo;
      if (napClassId != '') {
        this.classId = Number(napClassId);
      } else {
        this.classId = null;
      }
      if (napAge) this.age = Number(napAge);

      this.search();
    }
  }
})
</script>